import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import useU2000AxiosAuthenticated from "../u2000Core/axios/U2000UseAxiosAuthenticated";
import { Outlet } from "react-router-dom";
import i18next from "i18next";
import { useU2000InitLUHandlers } from "../u2000Core/hooks/handler/U2000UseInitLUHandlers";
import { U2000HandleApiError, type U2000ApiErrorResult } from "../u2000Core/axios/U2000HandleApiError";
import { useToast } from "../u2000Core/context/toast/U2000ToastContext";
import { doInitLU, luHandleAppiError, type U3105InitLU } from "./U3105Service";
import { U3105ContextProvider } from "./U3105Context";
import { useU2000LogicalUnitContext } from "../u2000Core/context/logicalUnit/U2000LogicalUnitContext";

export const NS = "u3105";

export const U3105ROLES = {
  READ: "U3105:READ",
  ADD: "U3105:ADD",
  EDIT: "U3105:EDIT",
  DELETE: "U3105:DELETE",
  AUDITS: "U3105:AUDITS",
} as const;
export type U3105ROLES = (typeof U3105ROLES)[keyof typeof U3105ROLES];

const defaultInitLU: U3105InitLU = {
  launchSearch: false,
  hideSearch: true,
  nbRecordPerPage: 25,
  logicalUnitCode: "U3105",
};

const U3105: React.FC = () => {
  const { i18n } = useTranslation();
  const [i18nReady, setI18nReady] = useState<boolean>(false);
  const { t } = useTranslation();
  const toast = useToast();
  const [initLUReady, setInitLUReady] = useState<boolean>(false);
  const u2000AxiosAuthenticated = useU2000AxiosAuthenticated();
  const [initLUData, setInitLUData] = useState<U3105InitLU>(defaultInitLU);

  useEffect(() => {
    i18next.loadNamespaces(["u3105"], (err) => {
      if (err) return console.error("Error loading namespace:", err);
      setI18nReady(true);
    });
  }, [i18n]);

  const handleInitLU = useU2000InitLUHandlers<U3105InitLU>({
    doInitLUFn: async () => {
      return await doInitLU(u2000AxiosAuthenticated);
    },
    onSuccess: (initLU: U3105InitLU) => {
      setInitLUData({
        ...defaultInitLU,
        ...initLU,
        logicalUnitCode: initLU.logicalUnitCode ?? "U3105",
      });
      setInitLUReady(true);
    },
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- aligné U3104
    onError: ({ error }: { error: any }) => {
      const errorResult = U2000HandleApiError(error, t, NS, luHandleAppiError) as U2000ApiErrorResult;
      if (errorResult.genericErrors?.length) {
        errorResult.genericErrors.forEach((err) => toast.error(err.text, err.title));
      }
      setInitLUReady(true);
    },
  });

  useEffect(() => {
    handleInitLU.getInitLU();
  }, [t]); // eslint-disable-line react-hooks/exhaustive-deps -- aligné U3104

  const { setLogicalUnitTitle, setLogicalUnitBreadcrumb } = useU2000LogicalUnitContext();
  useEffect(() => {
    if (!i18nReady || !initLUReady) return;
    setLogicalUnitTitle(t("title", { ns: NS }));

    return () => {
      setLogicalUnitTitle(null);
      setLogicalUnitBreadcrumb([]);
    };
  }, [i18nReady, initLUReady, t, setLogicalUnitTitle, setLogicalUnitBreadcrumb]);

  if (!i18nReady || !initLUReady) return null;

  return (
    <U3105ContextProvider initLU={initLUData}>
      <Outlet />
    </U3105ContextProvider>
  );
};

export default U3105;
